import { useState, useEffect, useMemo } from 'react'
import {
  Trash2,
  Clock,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  ChevronDown,
  Square,
  CheckSquare
} from 'lucide-react'
import {
  getClearedEmails,
  deleteClearedEmails,
  Email
} from '../../services/emailService'
import { extractCompanyDomain, getFaviconUrl } from '../../utils/domainUtils'

const ITEMS_PER_PAGE = 12

interface ClearedTabProps {
  userId: string
  onRefresh?: () => void
}

interface VendorGroup {
  vendorKey: string
  vendorName: string
  domain: string | null
  emails: Email[]
}

/**
 * Vendor logo component with favicon support and graceful fallback
 */
function VendorLogo({ name, domain, size = 6 }: { name: string; domain?: string | null; size?: number }) {
  const [imgError, setImgError] = useState(false)

  const sizeClass = size === 6 ? 'w-6 h-6' : size === 8 ? 'w-8 h-8' : 'w-10 h-10'
  const textSize = size === 6 ? 'text-xs' : size === 8 ? 'text-sm' : 'text-base'

  // Try to get favicon from domain using our smart domain utility
  if (domain && !imgError) {
    const faviconUrl = getFaviconUrl(domain, 64)
    return (
      <img
        src={faviconUrl}
        alt={name}
        className={`${sizeClass} rounded object-contain bg-gray-100`}
        onError={() => setImgError(true)}
      />
    )
  }

  return (
    <div className={`${sizeClass} rounded bg-gray-200 flex items-center justify-center text-gray-600 font-semibold ${textSize}`}>
      {name.charAt(0).toUpperCase()}
    </div>
  )
}

export default function ClearedTab({ userId, onRefresh }: ClearedTabProps) {
  const [clearedEmails, setClearedEmails] = useState<Email[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedVendors, setSelectedVendors] = useState<Set<string>>(new Set())
  const [deletingVendors, setDeletingVendors] = useState<Set<string>>(new Set())
  const [exitingVendors, setExitingVendors] = useState<Set<string>>(new Set())
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  useEffect(() => {
    loadClearedEmails()
  }, [userId])

  const loadClearedEmails = async () => {
    setLoading(true)
    try {
      const result = await getClearedEmails(500)
      if (result.success) {
        setClearedEmails(result.emails)
      }
    } catch (error) {
      console.error('Failed to load cleared emails:', error)
    } finally {
      setLoading(false)
    }
  }

  // Group emails by vendor (using fromName or from)
  const allVendorGroups = useMemo(() => {
    const groups = new Map<string, VendorGroup>()

    for (const email of clearedEmails) {
      // Use fromName if available, otherwise extract name from email
      const vendorName = email.fromName || email.from.split('@')[0] || 'Unknown'
      const vendorKey = vendorName.toLowerCase().trim()
      // Use smart domain extraction to get the actual company domain
      const domain = extractCompanyDomain(email.from)

      if (!groups.has(vendorKey)) {
        groups.set(vendorKey, {
          vendorKey,
          vendorName,
          domain,
          emails: []
        })
      }
      groups.get(vendorKey)!.emails.push(email)
    }

    // Sort by most recent email date and by number of emails
    return Array.from(groups.values()).sort((a, b) => {
      const aDate = a.emails[0]?.date instanceof Date ? a.emails[0].date : new Date(a.emails[0]?.date || 0)
      const bDate = b.emails[0]?.date instanceof Date ? b.emails[0].date : new Date(b.emails[0]?.date || 0)
      return bDate.getTime() - aDate.getTime()
    })
  }, [clearedEmails])

  // Pagination
  const totalPages = Math.ceil(allVendorGroups.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const displayGroups = allVendorGroups.slice(startIndex, startIndex + ITEMS_PER_PAGE)

  // Stats
  const totalEmailCount = clearedEmails.length
  const totalVendorCount = allVendorGroups.length

  // Get count of selected emails
  const selectedEmailCount = useMemo(() => {
    let count = 0
    for (const vendorKey of selectedVendors) {
      const group = allVendorGroups.find(g => g.vendorKey === vendorKey)
      if (group) {
        count += group.emails.length
      }
    }
    return count
  }, [selectedVendors, allVendorGroups])

  const handleSelectVendor = (vendorKey: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const newSelected = new Set(selectedVendors)
    if (newSelected.has(vendorKey)) {
      newSelected.delete(vendorKey)
    } else {
      newSelected.add(vendorKey)
    }
    setSelectedVendors(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedVendors.size === displayGroups.length) {
      setSelectedVendors(new Set())
    } else {
      setSelectedVendors(new Set(displayGroups.map(g => g.vendorKey)))
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedVendors.size === 0) return

    // Get all email IDs from selected vendors
    const emailIds: string[] = []
    const vendorKeys = Array.from(selectedVendors)
    for (const vendorKey of vendorKeys) {
      const group = allVendorGroups.find(g => g.vendorKey === vendorKey)
      if (group) {
        emailIds.push(...group.emails.map(e => e.id))
      }
    }

    if (!confirm(`Delete ${emailIds.length} email(s) from ${selectedVendors.size} vendor(s) permanently?`)) return

    // Start deleting animation
    setDeletingVendors(new Set(vendorKeys))

    try {
      const result = await deleteClearedEmails({ emailIds })
      if (result.success) {
        setActionResult({ type: 'success', message: `Deleted ${result.deletedCount} emails` })

        // Start exit animation
        setDeletingVendors(new Set())
        setExitingVendors(new Set(vendorKeys))

        // After exit animation, remove from state
        setTimeout(() => {
          setClearedEmails(prev => prev.filter(e => !emailIds.includes(e.id)))
          setSelectedVendors(new Set())
          setExitingVendors(new Set())
        }, 300)

        onRefresh?.()
      } else {
        setDeletingVendors(new Set())
        setActionResult({ type: 'error', message: 'Failed to delete emails' })
        setTimeout(() => setActionResult(null), 3000)
      }
    } catch (error) {
      console.error('Failed to delete emails:', error)
      setDeletingVendors(new Set())
      setActionResult({ type: 'error', message: 'Failed to delete emails' })
      setTimeout(() => setActionResult(null), 3000)
    }
  }

  const handleDeleteAll = async () => {
    if (clearedEmails.length === 0) return

    if (!confirm(`Delete ALL ${clearedEmails.length} cleared emails permanently? This cannot be undone.`)) return

    // Get all vendor keys for animation
    const vendorKeys = allVendorGroups.map(g => g.vendorKey)

    // Start deleting animation for all
    setDeletingVendors(new Set(vendorKeys))

    try {
      const result = await deleteClearedEmails({})
      if (result.success) {
        setActionResult({ type: 'success', message: `Deleted ${result.deletedCount} emails` })

        // Start exit animation for all
        setDeletingVendors(new Set())
        setExitingVendors(new Set(vendorKeys))

        // After exit animation, clear all
        setTimeout(() => {
          setClearedEmails([])
          setSelectedVendors(new Set())
          setExitingVendors(new Set())
        }, 300)

        onRefresh?.()
      } else {
        setDeletingVendors(new Set())
        setActionResult({ type: 'error', message: 'Failed to delete emails' })
        setTimeout(() => setActionResult(null), 3000)
      }
    } catch (error) {
      console.error('Failed to delete emails:', error)
      setDeletingVendors(new Set())
      setActionResult({ type: 'error', message: 'Failed to delete emails' })
      setTimeout(() => setActionResult(null), 3000)
    }
  }

  const handleTileClick = (group: VendorGroup) => {
    if (group.emails.length > 1) {
      setExpandedVendor(expandedVendor === group.vendorKey ? null : group.vendorKey)
    }
  }

  const formatDate = (date: Date | string) => {
    const d = date instanceof Date ? date : new Date(date)
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (hours < 1) return 'Just now'
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return d.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              Cleared Emails
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {totalEmailCount} {totalEmailCount === 1 ? 'email' : 'emails'} from {totalVendorCount} {totalVendorCount === 1 ? 'sender' : 'senders'}
              {totalPages > 1 && (
                <span className="text-gray-400"> • Page {currentPage} of {totalPages}</span>
              )}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {selectedVendors.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                disabled={deletingVendors.size > 0}
                className="flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 active:bg-red-800 disabled:opacity-50 transition-colors"
              >
                {deletingVendors.size > 0 ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete Selected ({selectedEmailCount})
              </button>
            )}
            {clearedEmails.length > 0 && (
              <button
                onClick={handleDeleteAll}
                disabled={deletingVendors.size > 0}
                className="flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] text-sm font-medium rounded-lg border border-red-300 text-red-700 hover:bg-red-50 active:bg-red-100 disabled:opacity-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete All
              </button>
            )}
          </div>
        </div>

        {actionResult && (
          <div className={`mt-3 p-3 rounded-lg text-xs sm:text-sm ${
            actionResult.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}>
            {actionResult.message}
          </div>
        )}
      </div>

      {/* Vendor Tiles Grid */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4">
        {allVendorGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 px-4 text-center">
            <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4 opacity-50" />
            <p className="text-base sm:text-lg font-medium">No cleared emails</p>
            <p className="text-xs sm:text-sm mt-1">Emails you clear will appear here</p>
          </div>
        ) : (
          <>
            {/* Select All Header */}
            <div className="mb-3 sm:mb-4 flex items-center gap-3">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 px-3 py-2.5 min-h-[44px] text-xs sm:text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors"
              >
                {selectedVendors.size === displayGroups.length && displayGroups.length > 0 ? (
                  <CheckSquare className="w-5 h-5 sm:w-4 sm:h-4 text-indigo-600" />
                ) : (
                  <Square className="w-5 h-5 sm:w-4 sm:h-4" />
                )}
                {selectedVendors.size > 0 ? `${selectedVendors.size} selected` : 'Select all on page'}
              </button>
            </div>

            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 transition-all duration-300">
              {displayGroups
                .filter(group => !exitingVendors.has(group.vendorKey) || deletingVendors.has(group.vendorKey))
                .map(group => (
                <VendorTile
                  key={group.vendorKey}
                  group={group}
                  isSelected={selectedVendors.has(group.vendorKey)}
                  isExpanded={expandedVendor === group.vendorKey}
                  isDeleting={deletingVendors.has(group.vendorKey)}
                  isExiting={exitingVendors.has(group.vendorKey)}
                  onSelect={(e) => handleSelectVendor(group.vendorKey, e)}
                  onTileClick={() => handleTileClick(group)}
                  formatDate={formatDate}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1 sm:gap-2 mt-4 sm:mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} />
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`min-w-[44px] h-11 px-3 text-sm font-medium rounded-lg transition-colors ${
                        currentPage === pageNum
                          ? 'bg-indigo-600 text-white'
                          : 'hover:bg-gray-100 active:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

interface VendorTileProps {
  group: VendorGroup
  isSelected: boolean
  isExpanded: boolean
  isDeleting: boolean
  isExiting: boolean
  onSelect: (e: React.MouseEvent) => void
  onTileClick: () => void
  formatDate: (date: Date | string) => string
}

function VendorTile({
  group,
  isSelected,
  isExpanded,
  isDeleting,
  isExiting,
  onSelect,
  onTileClick,
  formatDate
}: VendorTileProps) {
  const { vendorName, domain, emails } = group
  const hasMultiple = emails.length > 1
  const latestEmail = emails[0]

  return (
    <div className={`bg-white rounded-xl sm:rounded-2xl border-2 overflow-hidden transition-all duration-300 ${
      isExiting ? 'opacity-0 scale-95 pointer-events-none' :
      isDeleting ? 'animate-pulse ring-2 ring-red-400' :
      isSelected ? 'border-indigo-400 shadow-lg ring-2 ring-indigo-100 hover:shadow-lg' :
      isExpanded ? 'border-gray-300 shadow-lg hover:shadow-lg' :
      'border-gray-200 hover:border-gray-300 hover:shadow-lg'
    }`}>
      {/* Colored Header */}
      <div
        className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 sm:p-4 cursor-pointer"
        onClick={onTileClick}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white/90" />
              {hasMultiple && (
                <span className="px-2 py-0.5 bg-white/20 text-white text-[10px] sm:text-xs rounded-full font-medium">
                  {emails.length} emails
                </span>
              )}
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">
              CLEARED
            </h3>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Selection checkbox */}
            <button
              onClick={onSelect}
              className={`p-2 sm:p-1.5 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center rounded-lg transition-colors ${
                isSelected
                  ? 'bg-white text-indigo-600'
                  : 'bg-white/20 text-white hover:bg-white/30 active:bg-white/40'
              }`}
            >
              {isSelected ? (
                <CheckSquare size={20} className="sm:w-[18px] sm:h-[18px]" />
              ) : (
                <Square size={20} className="sm:w-[18px] sm:h-[18px]" />
              )}
            </button>
            {hasMultiple && (
              <ChevronDown className={`w-5 h-5 text-white/70 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            )}
          </div>
        </div>
      </div>

      {/* Vendor Info Section */}
      <div className="p-3 sm:p-4 cursor-pointer" onClick={onTileClick}>
        <div className="flex items-center gap-2 mb-2">
          <VendorLogo name={vendorName} domain={domain} size={6} />
          <span className="font-medium text-gray-900 truncate text-sm sm:text-base">{vendorName}</span>
        </div>

        <p className="text-gray-600 text-xs sm:text-sm line-clamp-2 mb-2 sm:mb-3">
          {latestEmail.subject}
        </p>

        <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Clock size={12} />
            {formatDate(latestEmail.date)}
          </span>
          <span className="flex items-center gap-1">
            <Mail size={12} />
            {emails.length} {emails.length === 1 ? 'email' : 'emails'}
          </span>
        </div>
      </div>

      {/* Expanded List of Emails */}
      {hasMultiple && isExpanded && (
        <div className="border-t border-gray-100 max-h-56 sm:max-h-64 overflow-y-auto">
          {emails.map((email, index) => (
            <div
              key={email.id}
              className={`p-3 hover:bg-gray-50 active:bg-gray-100 transition-colors ${
                index !== emails.length - 1 ? 'border-b border-gray-100' : ''
              }`}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                    {email.subject}
                  </p>
                  <p className="text-[10px] sm:text-xs text-gray-500">
                    {formatDate(email.date)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
